import { MDSGNBProps, MDSLNBItem, MDSLNBProps } from '@marqvision/mds-v2';
import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';

const getLNBInitialState = (storageKey: string): boolean => {
  if (typeof window === 'undefined') return true;

  try {
    const stored = sessionStorage.getItem(storageKey);
    return stored !== null ? JSON.parse(stored) : true;
  } catch {
    return true;
  }
};

const saveLNBState = (isOpen: boolean, storageKey: string): void => {
  if (typeof window === 'undefined') return;

  try {
    sessionStorage.setItem(storageKey, JSON.stringify(isOpen));
  } catch {
    // sessionStorage를 사용할 수 없는 경우 무시
  }
};

export const useNavigationProps = (params?: {
  onLNBOpenChange?: (value: boolean) => void;
  onLNBItemClick?: (item: Partial<MDSLNBItem>, isLNBOpen: boolean) => void;
  /**
   * LNB fold를 허용할 경로들
   * 지정된 경로에서만 shouldCollapse 메뉴 클릭 시 LNB가 접힘
   * 기본값: ['/home']
   */
  foldableFromPaths?: string[];
  /**
   * LNB 상태 저장 설정
   */
  persistence?: {
    /**
     * sessionStorage 키
     */
    storageKey: string;
  };
}) => {
  const { pathname } = useLocation();

  const foldableFromPaths = params?.foldableFromPaths;
  const hasPersistence = !!params?.persistence;
  const storageKey = params?.persistence?.storageKey;

  const [isLNBOpen, setIsLNBOpen] = useState<boolean>(() =>
    hasPersistence && storageKey ? getLNBInitialState(storageKey) : true
  );

  const toggleLNB = () => {
    setIsLNBOpen((prev) => {
      const newState = !prev;
      params?.onLNBOpenChange?.(newState);
      if (hasPersistence && storageKey) {
        saveLNBState(newState, storageKey);
      }
      return newState;
    });
  };

  const foldLNB = () => {
    const isFoldablePath = foldableFromPaths ? foldableFromPaths.includes(pathname) : true;

    if (isFoldablePath) {
      setIsLNBOpen(false);
      params?.onLNBOpenChange?.(false);
    }
  };

  const clickLNBItem = params?.onLNBItemClick
    ? (item: Partial<MDSLNBItem>) => {
        params.onLNBItemClick?.(item, isLNBOpen);
      }
    : undefined;

  const lnbProps: Omit<MDSLNBProps, 'list'> = {
    isOpen: isLNBOpen,
    value: pathname,
    LinkComponent: Link,
    onFold: foldLNB,
    onItemClick: clickLNBItem,
  };

  const gnbProps: MDSGNBProps = {
    isLNBOpen,
    onLNBToggle: toggleLNB,
    LinkComponent: Link,
  };

  return {
    lnbProps,
    gnbProps,
  };
};

export type UseNavigationProps = ReturnType<typeof useNavigationProps>;
